import { Box, Stack, Typography } from '@mui/material';
import { trim } from 'lodash';
import { useEffect } from 'react';

import { Accordion } from '@/components/Accordion/Accordion';
import { Button } from '@/components/form/baseInputs/Button';
import { Link } from '@/components/form/baseInputs/Link';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { IntegrationConfigurationAddeparClientType } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { ADDEPAR_IMPORT_TYPE_DISPLAY } from './graphql/AdminIntegrationsPage.constants';
import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useUpdateIntegrationConfigurationMutation,
} from './graphql/AdminIntegrationsPage.generated';

export function isAddeparConfigured(
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null
) {
  return (
    Boolean(configuration?.addeparFirmID) &&
    Boolean(configuration?.addeparSubdomain)
  );
}

const CUSTOM_ATTRIBUTE_PREFIX = '_custom';

const customAttributeValidation = {
  mustStartWithCustomPrefix: (value: string | boolean) => {
    if (typeof value !== 'string') return true;
    return (
      !value ||
      value.startsWith(CUSTOM_ATTRIBUTE_PREFIX) ||
      `Must start with "${CUSTOM_ATTRIBUTE_PREFIX}"`
    );
  },
};

const internalOnlyOptions = [
  IntegrationConfigurationAddeparClientType.All,
  IntegrationConfigurationAddeparClientType.Group,
];

function getClientImportTypeOptions(isInternalUser: boolean) {
  return [
    IntegrationConfigurationAddeparClientType.Client,
    IntegrationConfigurationAddeparClientType.Household,
    IntegrationConfigurationAddeparClientType.Group,
    IntegrationConfigurationAddeparClientType.All,
  ].map((v) => {
    const isInternalOnlyOption = internalOnlyOptions.includes(v);
    return {
      value: v,
      label: ADDEPAR_IMPORT_TYPE_DISPLAY[v],
      disabled: isInternalOnlyOption && !isInternalUser,
      helpText:
        isInternalOnlyOption && isInternalUser
          ? "(Internal) Use this if you are certain it's what you need."
          : undefined,
    };
  });
}

interface AddeparIntegrationConfigurationFormShape {
  firmId: string;
  subdomain: string;
  clientImportType: IntegrationConfigurationAddeparClientType;
  lookThroughCompositeSecurities: boolean;
  customAssetClassName: string;
  customAssetNameAPIField: string;
}

type AddeparIntegrationConfigurationFormPaths =
  PathsOf<AddeparIntegrationConfigurationFormShape>;

interface AddeparIntegrationFormProps {
  onEditComplete: () => void;
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment;
  disabled?: boolean;
}

export function AddeparIntegrationForm({
  onEditComplete,
  configuration,
  disabled = false,
}: AddeparIntegrationFormProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const currentUser = useCurrentUser();
  const [updateConfiguration] = useUpdateIntegrationConfigurationMutation({
    onCompleted: () => {
      showFeedback('Addepar configuration updated successfully', {
        variant: 'success',
      });
      onEditComplete();
    },
    onError: (err) => {
      showFeedback(
        'Failed to update Addepar configuration. Please refresh the page and try again.'
      );
      reportError('Failed to update Addepar configuration', err);
    },
  });
  const formMethods = useForm<AddeparIntegrationConfigurationFormShape>({
    defaultValues: {
      firmId: '',
      subdomain: '',
      clientImportType: IntegrationConfigurationAddeparClientType.All,
      lookThroughCompositeSecurities: true,
      customAssetClassName: '',
    },
  });
  const { control, handleSubmit, reset, watch } = formMethods;
  const subdomain = watch('subdomain');

  useEffect(() => {
    reset({
      firmId: configuration.addeparFirmID ?? '',
      subdomain: configuration.addeparSubdomain ?? '',
      clientImportType:
        configuration.addeparClientType ??
        IntegrationConfigurationAddeparClientType.All,
      lookThroughCompositeSecurities:
        configuration.addeparLookThroughCompositeSecurities ?? true,
      customAssetClassName: configuration.addeparCustomAssetClassName ?? '',
      customAssetNameAPIField:
        configuration.addeparCustomAssetNameAPIField ?? '',
    });
  }, [configuration, reset]);

  const onSubmit = handleSubmit((data) => {
    return updateConfiguration({
      variables: {
        input: {
          id: configuration?.id,
          update: {
            addeparFirmID: trim(data.firmId),
            // the backend requires that the subdomain is lowercase, and this is a more
            // user-friendly way to handle this rather than showing the user a validation error
            addeparSubdomain: trim(data.subdomain).toLowerCase(),
            addeparClientType: data.clientImportType,
            addeparLookThroughCompositeSecurities:
              data.lookThroughCompositeSecurities,
            addeparCustomAssetClassName: data.customAssetClassName
              ? trim(data.customAssetClassName)
              : null,
            clearAddeparCustomAssetClassName: !data.customAssetClassName,
            addeparCustomAssetNameAPIField: data.customAssetNameAPIField
              ? trim(data.customAssetNameAPIField)
              : null,
            clearAddeparCustomAssetNameAPIField: !data.customAssetNameAPIField,
          },
        },
      },
    });
  });

  return (
    <Stack spacing={2}>
      <FormFieldsDisabledProvider isDisabled={disabled}>
        <FormAwareTextInput
          control={control}
          fieldName={
            'firmId' as const satisfies AddeparIntegrationConfigurationFormPaths
          }
          label="Firm ID"
          helpText="This is the unique identifier for your Addepar firm. If you don't know it, please contact Addepar support."
          validation={{
            mustBeNumber: (value: string | boolean) =>
              typeof value !== 'string' ||
              /^\d+$/.test(value) ||
              'Firm ID must be a number',
          }}
          required
        />
        <FormAwareTextInput
          control={control}
          fieldName={
            'subdomain' as const satisfies AddeparIntegrationConfigurationFormPaths
          }
          helpText="This is the subdomain you use to access Addepar. For example, if you access Addepar at 'https://terrabella.addepar.com', your subdomain is 'terrabella'."
          label="Subdomain"
          required
        />
        <Accordion variant="default" title="Advanced settings">
          <Stack spacing={2}>
            <FormAwareRadioGroup
              control={control}
              row={false}
              required
              stackingDirection="row"
              helpText="How your clients are modeled in Addepar. For example, if you choose 'Groups', only Addepar 'Groups' will be eligible to be imported as Luminary clients."
              label="Client import type"
              fieldName={
                'clientImportType' as const satisfies AddeparIntegrationConfigurationFormPaths
              }
              options={getClientImportTypeOptions(
                currentUser?.isInternalUser ?? false
              )}
            />
            <FormAwareSwitch<AddeparIntegrationConfigurationFormShape>
              control={control}
              label="Look through composite securities"
              labelPosition="right"
              helpText="When enabled, Addepar values are synced by looking through entities like pooled vehicles to their underlying holdings. When disabled, the value on the owner/pooled vehicle position is used."
              fieldName={
                'lookThroughCompositeSecurities' as const satisfies AddeparIntegrationConfigurationFormPaths
              }
            />
            <Stack spacing={1}>
              <Typography variant="title-l">Custom attributes</Typography>
              <FormAwareTextInput
                control={control}
                fieldName={
                  'customAssetClassName' as const satisfies AddeparIntegrationConfigurationFormPaths
                }
                helpText="The API field name of the custom asset class attribute"
                label="Asset class attribute (optional)"
                validation={customAttributeValidation}
              />
              <FormAwareTextInput
                control={control}
                fieldName={
                  'customAssetNameAPIField' as const satisfies AddeparIntegrationConfigurationFormPaths
                }
                helpText="The API field for custom asset names"
                label="Asset name attribute (optional)"
                validation={customAttributeValidation}
              />
              <Box height={16} />
              <Callout severity="info-low">
                <Box>
                  The above are fields to enter custom attributes from Addepar.
                  It is labeled as &quot;API field name&quot; in your Addepar
                  instance and starts with &quot;_custom&quot;.
                </Box>
                {subdomain && (
                  <Box mt={0.5}>
                    <Link
                      display="View Addepar attributes"
                      href={`https://${subdomain}.addepar.com/app/settings/attributes`}
                      external
                    />
                  </Box>
                )}
              </Callout>
            </Stack>
          </Stack>
        </Accordion>
      </FormFieldsDisabledProvider>

      <Button
        size="sm"
        variant="primary"
        onClick={onSubmit}
        disabled={disabled}
      >
        Save configuration
      </Button>
    </Stack>
  );
}
